import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

// Reusable "old vs new" resolver for wherever a proposed value can collide
// with what's already there (see the backend's ProfileFieldDiff): the
// admin's AI-research-review screen (SpeakerDetail.js), the admin's
// invite-time heads-up (informational only there now - see
// SpeakerProfileService.previewInvite), and the speaker's own accept-time
// review of what an admin staged for them (SpeakerInviteConfirmation.js /
// SpeakerProfileService.pendingProfileReview+resolvePendingProfileReview) -
// that last one is the only place any of this data ever actually reaches a
// live Account. Per docs/profile-unification-design.md there is never a
// silent overwrite - a field with nothing there yet is just shown as "will
// be filled in" (nothing to decide), everything else needs an explicit pick.
// Exported so a plain, read-only rendering of a diff (see SpeakerDetail.js's
// invite-time heads-up, which no longer lets the admin decide) can label
// fields the same way this picker does.
export const FIELD_LABELS = {
  fullName: 'speaker.field.name', email: 'speaker.field.email', title: 'speaker.field.title',
  specialty: 'speaker.field.specialty', institution: 'speaker.field.institution', city: 'speaker.field.city',
  country: 'speaker.field.country', photoUrl: 'speaker.field.photoUrl', linkedInUrl: 'speaker.field.linkedin',
  facebookUrl: 'speaker.field.facebook', instagramUrl: 'speaker.field.instagram', websiteUrl: 'speaker.field.website',
  shortBioDe: 'speaker.field.shortBioDe', shortBioEn: 'speaker.field.shortBioEn',
  motivationDe: 'speaker.field.motivationShortDe', motivationEn: 'speaker.field.motivationShortEn',
  professionalStatuses: 'speakerMgmt.statusCsv', interests: 'speakerMgmt.interestsCsv',
};

const Wrap = styled.div`display:flex;flex-direction:column;gap:.6rem;`;
const Row = styled.div`border:1px solid #e2e8f0;padding:.7rem .85rem;background:#fff;`;
const RowLabel = styled.div`font-weight:700;font-size:.85rem;color:#334155;margin-bottom:.45rem;`;
const Options = styled.div`display:flex;gap:.6rem;flex-wrap:wrap;`;
const Option = styled.label`flex:1;min-width:12rem;display:flex;flex-direction:column;gap:.3rem;padding:.55rem .65rem;border:1px solid ${p=>p.$active?'#47ABDB':'#e2e8f0'};background:${p=>p.$active?'#eff9fe':'#f8fafc'};cursor:pointer;font-size:.8rem;`;
const OptionHead = styled.div`display:flex;align-items:center;gap:.4rem;font-weight:700;color:#0f172a;`;
const OptionValue = styled.div`color:#334155;white-space:pre-wrap;word-break:break-word;font-weight:400;padding-left:1.3rem;`;
const AutoTag = styled.div`font-size:.72rem;font-weight:700;color:#166534;background:#dcfce7;display:inline-block;padding:.15rem .5rem;margin-bottom:.45rem;`;

// Sensible default so a submit without touching anything never overwrites
// real data: a genuine conflict defaults to keeping what's already there.
export const defaultResolutions = diffs =>
  Object.fromEntries((diffs || []).map(d => [d.field, d.currentValue ? 'CURRENT' : 'PROPOSED']));

const FieldDiffPicker = ({ diffs, resolutions, onChange }) => {
  const intl = useIntl();
  const t = (id, def) => intl.formatMessage({ id, defaultMessage: def });
  const label = field => t(FIELD_LABELS[field] || field, field);
  const set = (field, value) => onChange({ ...resolutions, [field]: value });

  return (
    <Wrap>
      {(diffs || []).map(d => {
        if (!d.currentValue) {
          return (
            <Row key={d.field}>
              <RowLabel>{label(d.field)}</RowLabel>
              <AutoTag><FormattedMessage id="speakerMgmt.diff.willFill" defaultMessage="Will be filled in — nothing to overwrite" /></AutoTag>
              <div style={{ fontSize: '.82rem', color: '#334155', whiteSpace: 'pre-wrap' }}>{d.proposedValue}</div>
            </Row>
          );
        }
        const choice = resolutions[d.field] || 'CURRENT';
        return (
          <Row key={d.field}>
            <RowLabel>{label(d.field)}</RowLabel>
            <Options>
              <Option $active={choice === 'CURRENT'} onClick={() => set(d.field, 'CURRENT')}>
                <OptionHead><input type="radio" readOnly checked={choice === 'CURRENT'} /><FormattedMessage id="speakerMgmt.diff.keepCurrent" defaultMessage="Keep current" /></OptionHead>
                <OptionValue>{d.currentValue}</OptionValue>
              </Option>
              <Option $active={choice === 'PROPOSED'} onClick={() => set(d.field, 'PROPOSED')}>
                <OptionHead><input type="radio" readOnly checked={choice === 'PROPOSED'} /><FormattedMessage id="speakerMgmt.diff.useNew" defaultMessage="Use new value" /></OptionHead>
                <OptionValue>{d.proposedValue}</OptionValue>
              </Option>
            </Options>
          </Row>
        );
      })}
    </Wrap>
  );
};

export default FieldDiffPicker;
